export const AUTHORIZE_START_GUIDE_STEP_IDS = {
  BUSINESS: "business",
  PAYMENTS: "payments",
  PACKAGING: "packaging",
  LABEL: "label",
  DECLARATION: "declaration",
  REPRESENTATIVE: "representative",
  HELP: "help",
};

export const authorizeStartGuideContent = {
  texts: {
    subtitle:
      "The top left corner should say L'Authorize. If it does not, click the 9-dot icon in the top right and select L'Authorize.",
    allDone: "You've completed the L'Authorize setup tasks.",
    showPreviousTasks: "Show previous tasks",
    tasksLeft: ({ tasksLeft, minutesLeft }) =>
      `${tasksLeft} tasks left (About ${minutesLeft} minutes)`,
    goal: "Use this guide to publish your first digital packaging label.",
    footerLinks: [
      { label: "Open Help Center", to: "/help" },
      { label: "Go to Packaging", to: "/packaging" },
    ],
    nextActions: [
      {
        title: "Review packaging labels",
        description: "Print a saved label or copy its digital link.",
        label: "Go to Packaging",
        to: "/packaging",
        variant: "primary",
      },
      {
        title: "Check document requests",
        description: "Requests for an authorised representative appear in Messages.",
        label: "Open Messages",
        to: "/messages?tab=documentRequests",
      },
    ],
  },
  minutesPerTask: 5,
  defaultSteps: [
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.BUSINESS,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.BUSINESS,
      name: "Business details",
      category: "required",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.PAYMENTS,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.PAYMENTS,
      name: "Payment details",
      category: "required",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.PACKAGING,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.PACKAGING,
      name: "Add packaging",
      category: "required",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.LABEL,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.LABEL,
      name: "Digital label (PPWR)",
      category: "required",
      requires: [AUTHORIZE_START_GUIDE_STEP_IDS.PACKAGING],
      lockReason: "Save the packaging and mark it Active before creating a digital label.",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.DECLARATION,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.DECLARATION,
      name: "Declaration of conformity",
      category: "recommended",
      optional: true,
      requires: [AUTHORIZE_START_GUIDE_STEP_IDS.LABEL],
      lockReason: "Create the digital label first. The declaration PDF is uploaded on its Files step.",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.REPRESENTATIVE,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.REPRESENTATIVE,
      name: "Authorised representative",
      category: "recommended",
      optional: true,
      requires: [AUTHORIZE_START_GUIDE_STEP_IDS.LABEL],
      lockReason: "Complete the label form first. The document request then appears in Messages.",
      complete: false,
    },
    {
      id: AUTHORIZE_START_GUIDE_STEP_IDS.HELP,
      step: AUTHORIZE_START_GUIDE_STEP_IDS.HELP,
      name: "Help",
      category: "learn",
      complete: false,
    },
  ],
  steps: {
    [AUTHORIZE_START_GUIDE_STEP_IDS.BUSINESS]: {
      name: "Business details",
      description:
        "Open Business settings, then Business. Enter the company details in English and without special characters. These details are used for filing declarations.",
      to: "/business-settings?tab=business",
      actionLabel: "Go to Business settings",
      actions: [
        {
          label: "Go to Business settings",
          to: "/business-settings?tab=business",
          variant: "primary",
        },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.PAYMENTS]: {
      name: "Payment details",
      description:
        "In Payments, specify the company that pays for the subscription and receives invoices, and select a currency. If that company is based in the UK, the currency choice is not available: payments are always in pounds sterling.",
      to: "/product-settings/payments",
      actionLabel: "Go to Payments",
      actions: [
        {
          label: "Go to Payments",
          to: "/product-settings/payments",
          variant: "primary",
        },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.PACKAGING]: {
      name: "Add packaging",
      description:
        "Go to Packaging and click + Add packaging. You can skip steps when you do not have the information. If the packaging is single-use and not made from recycled materials, skip eco-modulation: that data does not change the eco-fee.",
      to: "/packaging/new",
      actionLabel: "Add packaging",
      actions: [
        { label: "Add packaging", to: "/packaging/new", variant: "primary" },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.LABEL]: {
      name: "Digital label (PPWR)",
      description:
        "Create a label only after the packaging is saved and marked Active. In Packaging, click the three dots next to the item, select Edit, and open the Digital label (PPWR) tab. Fill in every step. You can then print the label or copy its digital link.",
      to: "/packaging",
      actionLabel: "Go to Packaging",
      actions: [
        { label: "Go to Packaging", to: "/packaging", variant: "primary" },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.DECLARATION]: {
      name: "Declaration of conformity",
      description:
        "Optional. If the declaration should open from the QR code together with the label, upload a PDF of your existing Declaration of Conformity on the Files step of the Digital label (PPWR) form.",
      to: "/packaging",
      actionLabel: "Go to Packaging",
      actions: [
        { label: "Go to Packaging", to: "/packaging", variant: "primary" },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.REPRESENTATIVE]: {
      name: "Authorised representative",
      description:
        "Optional. If you appoint Lappa as your Authorised Representative, a document request appears in Messages shortly after you complete the label form. Upload the files there. After the documents are verified, the label goes live online.",
      to: "/messages?tab=documentRequests",
      actionLabel: "Open Messages",
      actions: [
        {
          label: "Open Messages",
          to: "/messages?tab=documentRequests",
          variant: "primary",
        },
      ],
    },
    [AUTHORIZE_START_GUIDE_STEP_IDS.HELP]: {
      name: "Help",
      description:
        "While filling in a form, use the live chat in the bottom-right corner. Specialists are available Monday to Friday, 9:00–17:00 UK time. Outside those hours, open a case in the Help Center. On some plans the Help Center also shows your account manager.",
      to: "/help",
      actionLabel: "Open Help Center",
      actions: [
        { label: "Open Help Center", to: "/help", variant: "primary" },
      ],
    },
  },
};
